import React from 'react';
import classNames from 'classnames';
import { LinkedLogo } from '../../../../components';

import Field from '../../Field';
import BlockBuilder from '../../BlockBuilder';

import SectionContainer from '../SectionContainer';
import css from './SectionFooter.module.css';
import { useStore } from 'react-redux';

// The number of columns (numberOfColumns) affects styling

const GRID_CONFIG = [
  { contentCss: css.contentCol1, gridCss: css.gridCol1 },
  { contentCss: css.contentCol2, gridCss: css.gridCol2 },
  { contentCss: css.contentCol3, gridCss: css.gridCol3 },
  { contentCss: css.contentCol4, gridCss: css.gridCol4 },
];
const MAX_MOBILE_SCREEN_WIDTH = 1024;

const getIndex = numberOfColumns => numberOfColumns - 1;

const getContentCss = numberOfColumns => {
  const contentConfig = GRID_CONFIG[getIndex(numberOfColumns)];
  return contentConfig ? contentConfig.contentCss : GRID_CONFIG[0].contentCss;
};

const getGridCss = numberOfColumns => {
  const contentConfig = GRID_CONFIG[getIndex(numberOfColumns)];
  return contentConfig ? contentConfig.gridCss : GRID_CONFIG[0].gridCss;
};

/**
 * @typedef {Object} SocialMediaLinkConfig
 * @property {'socialMediaLink'} fieldType
 * @property {string} platform
 * @property {string} url
 */

/**
 * @typedef {Object} BlockConfig
 * @property {string} blockId
 * @property {string} blockName
 * @property {'defaultBlock' | 'footerBlock' | 'socialMediaLink'} blockType
 */

/**
 * @typedef {Object} FieldComponentConfig
 * @property {ReactNode} component
 * @property {Function} pickValidProps
 */

/**
 * Section component that's able to show blocks in multiple different columns (defined by "numberOfColumns" prop)
 *
 * @component
 * @param {Object} props
 * @param {string?} props.className add more style rules in addition to components own css.root
 * @param {string?} props.rootClassName overwrite components own css.root
 * @param {string} props.sectionId id of the section
 * @param {'footer'} props.sectionType
 * @param {number} props.numberOfColumns columns for blocks in footer (1-4)
 * @param {Array<SocialMediaLinkConfig>?} props.socialMediaLinks array of social media link configs
 * @param {Object?} props.slogan
 * @param {Object?} props.copyright
 * @param {Object?} props.appearance
 * @param {Array<BlockConfig>?} props.blocks array of block configs
 * @param {Object} props.options extra options for the section component (e.g. custom fieldComponents)
 * @param {Object<string,FieldComponentConfig>?} props.options.fieldComponents custom fields
 * @returns {JSX.Element} Section for article content
 */
const SectionFooter = props => {
  const {
    sectionId,
    className,
    rootClassName,
    numberOfColumns = 1,
    socialMediaLinks = [],
    slogan,
    appearance,
    copyright,
    blocks = [],
    options,
    linkLogoToExternalSite,
  } = props;

  // If external mapping has been included for fields
  // E.g. { h1: { component: MyAwesomeHeader } }
  const fieldComponents = options?.fieldComponents;
  const fieldOptions = { fieldComponents };
  const linksWithBlockId = socialMediaLinks?.map(sml => {
    return {
      ...sml,
      blockId: sml.link.platform,
    };
  });

  const hasMatchMedia = typeof window !== 'undefined' && window?.matchMedia;
  const isMobileLayout = hasMatchMedia
    ? window.matchMedia(`(max-width: ${MAX_MOBILE_SCREEN_WIDTH}px)`)?.matches
    : true;
  const logoLayout = isMobileLayout ? 'mobile' : 'desktop';

  // use block builder instead of mapping blocks manually

  const fbLink = linksWithBlockId?.find(l => l?.link?.platform === 'facebook');
  const igLink = linksWithBlockId?.find(l => l?.link?.platform === 'instagram');
  const pinLink = linksWithBlockId?.find(l => l?.link?.platform === 'pinterest');
  const twLink = linksWithBlockId?.find(l => l?.link?.platform === 'twitter');
  const state = useStore()?.getState?.();

  const isAuthenticated = state?.auth?.isAuthenticated;
  const createAccountBlock = blocks?.find(b => b?.blockId === 'create_account');
  const createAccountTitle = createAccountBlock?.blockName;

  const generalBlock = blocks?.find(b => b?.blockId === 'general');
  generalBlock.text.content =
    state?.user?.currentUser?.attributes?.profile?.publicData?.userType === 'parent'
      ? generalBlock?.text?.content?.replace('[Post a new listing](/l/new)', '')
      : generalBlock?.text?.content;
  const generalTitle = generalBlock?.blockName;

  const legalBlock1 = blocks?.find(b => b?.blockId === 'legal-1');
  const legalBlock2 = blocks?.find(b => b?.blockId === 'legal-2');
  return (
    <SectionContainer
      as="footer"
      id={sectionId}
      className={className || css.root}
      rootClassName={rootClassName}
      appearance={appearance}
      options={fieldOptions}
      containerClassName={css.container}
    >
      <div className={css.footer}>
        <div className={classNames(css.content, getContentCss(numberOfColumns))}>
          <div>
            <LinkedLogo
              rootClassName={css.logoLink}
              logoClassName={css.logoWrapper}
              logoImageClassName={css.logoImage}
              linkToExternalSite={linkLogoToExternalSite}
              layout={logoLayout}
            />
          </div>

          <div
            style={{
              display: 'flex',
              flexDirection: 'row',
              gap: 12,
              gridArea: 'legal',
              marginTop: 'auto',
              marginLeft: 'auto',
            }}
          >
            {legalBlock1 && (
              <BlockBuilder blocks={[legalBlock1]} sectionId={sectionId + 'legalBlock1'} />
            )}
            {legalBlock2 && (
              <BlockBuilder blocks={[legalBlock2]} sectionId={sectionId + 'legalBlock2'} />
            )}
          </div>

          <div className={css.sloganMobile}>
            <Field data={slogan} className={css.slogan} />
          </div>

          <div className={css.detailsInfo}>
            <div className={css.sloganDesktop}>
              <Field data={slogan} className={css.slogan} />
            </div>
            <div
              style={{
                display: 'flex',
                flexDirection: 'row',
                gap: 12,
                marginTop: 120,
              }}
            >
              {fbLink && (
                <LinkIcon link={fbLink?.link?.url} target="_blank">
                  <svg
                    width="32"
                    height="32"
                    viewBox="0 0 32 32"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M21.8501 2.66675H10.1501C6.02448 2.66675 2.66675 6.02448 2.66675 10.1501V21.8501C2.66675 25.9757 6.02448 29.3334 10.1501 29.3334H21.8501C25.9757 29.3334 29.3334 25.9757 29.3334 21.8501V10.1501C29.3334 6.02448 25.9757 2.66675 21.8501 2.66675ZM27.3812 21.8501C27.3812 24.8955 24.8955 27.3812 21.8501 27.3812H20.1582V18.154H23.6591V15.5511H20.1582V13.6119C20.1582 13.3516 20.2103 12.5187 20.6007 12.1153C20.7048 11.9981 20.913 11.855 21.3816 11.855H23.6721V9.25207H21.3816C20.3014 9.25207 19.3773 9.62949 18.7136 10.3323C17.4902 11.6207 17.5553 13.5338 17.5683 13.677V15.5511H14.0674V18.154H17.5683V27.3812H10.1631C7.1177 27.3812 4.63193 24.8955 4.63193 21.8501V10.1501C4.61892 7.10469 7.10469 4.61892 10.1501 4.61892H21.8501C24.8955 4.61892 27.3812 7.10469 27.3812 10.1501V21.8501Z"
                      fill="#D2552B"
                    />
                  </svg>
                </LinkIcon>
              )}
              {igLink && (
                <LinkIcon link={igLink?.link?.url}>
                  <svg
                    width="32"
                    height="32"
                    viewBox="0 0 32 32"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M21.8501 2.66675H10.1501C6.02448 2.66675 2.66675 6.02448 2.66675 10.1501V21.8501C2.66675 25.9757 6.02448 29.3334 10.1501 29.3334H21.8501C25.9757 29.3334 29.3334 25.9757 29.3334 21.8501V10.1501C29.3334 6.02448 25.9757 2.66675 21.8501 2.66675ZM27.3812 21.8501C27.3812 24.8955 24.8955 27.3812 21.8501 27.3812H10.1501C7.10469 27.3812 4.61892 24.8955 4.61892 21.8501V10.1501C4.61892 7.10469 7.10469 4.61892 10.1501 4.61892H21.8501C24.8955 4.61892 27.3812 7.10469 27.3812 10.1501V21.8501Z"
                      fill="#D2552B"
                    />
                    <path
                      d="M16.0065 9.74658C12.5577 9.74658 9.74658 12.5577 9.74658 16.0065C9.74658 19.4554 12.5577 22.2665 16.0065 22.2665C19.4554 22.2665 22.2665 19.4554 22.2665 16.0065C22.2665 12.5577 19.4554 9.74658 16.0065 9.74658ZM16.0065 20.3013C13.6379 20.3013 11.6988 18.3752 11.6988 15.9935C11.6988 13.6119 13.6249 11.6857 16.0065 11.6857C18.3882 11.6857 20.3143 13.6119 20.3143 15.9935C20.3143 18.3752 18.3882 20.3013 16.0065 20.3013Z"
                      fill="#D2552B"
                    />
                    <path
                      d="M23.0343 7.31299C22.1623 7.31299 21.4465 8.02878 21.4465 8.90075C21.4465 9.77272 22.1623 10.4885 23.0343 10.4885C23.9063 10.4885 24.6221 9.77272 24.6221 8.90075C24.6221 8.02878 23.9063 7.31299 23.0343 7.31299Z"
                      fill="#D2552B"
                    />
                  </svg>
                </LinkIcon>
              )}

              {pinLink && (
                <LinkIcon link={pinLink?.link?.url}>
                  <svg
                    width="32"
                    height="32"
                    viewBox="0 0 32 32"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M16.3708 8.35415C12.037 8.35415 9.85059 11.3605 9.85059 13.8723C9.85059 15.395 10.4493 16.7485 11.7247 17.243C11.7767 17.2691 11.8288 17.2691 11.8808 17.2691C12.024 17.2691 12.1411 17.178 12.1802 17.0218C12.2192 16.8656 12.3233 16.4752 12.3624 16.306C12.4275 16.0848 12.4014 16.0067 12.2322 15.8115C11.8678 15.395 11.6336 14.8484 11.6336 14.0805C11.6336 11.842 13.3645 9.85082 16.1236 9.85082C18.5703 9.85082 19.9238 11.2954 19.9238 13.2346C19.9238 15.7854 18.7655 17.9328 17.0346 17.9328C16.0845 17.9328 15.3687 17.165 15.59 16.2279C15.8633 15.1087 16.3969 13.8983 16.3969 13.0914C16.3969 12.3626 15.9934 11.7639 15.1605 11.7639C14.1844 11.7639 13.4035 12.74 13.4035 14.0545C13.4035 14.8874 13.6899 15.447 13.6899 15.447C13.6899 15.447 12.6877 19.5466 12.5186 20.2624C12.1672 21.694 12.4665 23.4379 12.4925 23.6201C12.4925 23.6852 12.5576 23.7112 12.6097 23.7112C12.6487 23.7112 12.6877 23.6982 12.7138 23.6592C12.8049 23.542 13.9632 22.1625 14.3666 20.77C14.4837 20.3795 15.0043 18.3363 15.0043 18.3363C15.3167 18.9219 16.2537 19.4425 17.2298 19.4425C20.1581 19.4425 22.1493 16.8526 22.1493 13.3908C22.1493 10.7748 19.8587 8.32812 16.3708 8.32812V8.35415Z"
                      fill="#D2552B"
                    />
                    <path
                      d="M21.8498 2.66675H10.1498C6.02424 2.66675 2.6665 6.02448 2.6665 10.1501V21.8501C2.6665 25.9757 6.02424 29.3334 10.1498 29.3334H21.8498C25.9754 29.3334 29.3332 25.9757 29.3332 21.8501V10.1501C29.3332 6.02448 25.9754 2.66675 21.8498 2.66675ZM27.381 21.8501C27.381 24.8955 24.8952 27.3812 21.8498 27.3812H10.1498C7.10444 27.3812 4.61868 24.8955 4.61868 21.8501V10.1501C4.61868 7.10469 7.10444 4.61892 10.1498 4.61892H21.8498C24.8952 4.61892 27.381 7.10469 27.381 10.1501V21.8501Z"
                      fill="#D2552B"
                    />
                  </svg>
                </LinkIcon>
              )}
              {twLink && (
                <LinkIcon link={twLink?.link?.url}>
                  <svg
                    width="32"
                    height="32"
                    viewBox="0 0 32 32"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M18.5332 13.9601L28.4665 2.66675H26.1198L17.5065 12.4667L10.6132 2.66675H2.6665L13.0798 17.4934L2.6665 29.3334H5.01317L14.1198 18.9734L21.3865 29.3334H29.3332L18.5332 13.9601ZM15.3198 17.6267L14.2665 16.1467L5.8665 4.40008H9.47984L16.2532 13.8801L17.3065 15.3601L26.1065 27.6801H22.4932L15.3065 17.6267H15.3198Z"
                      fill="#D2552B"
                    />
                  </svg>
                </LinkIcon>
              )}
            </div>

            <Field data={copyright} className={css.copyright} />
          </div>

          <div className={classNames(css.grid, getGridCss(numberOfColumns))}>
            {generalBlock && (
              <SectionWrapper>
                <LinkSectionHeader title={generalTitle} />
                <BlockBuilder blocks={[generalBlock]} sectionId={sectionId} options={options} />
              </SectionWrapper>
            )}
            {createAccountBlock && !isAuthenticated && (
              <SectionWrapper>
                <LinkSectionHeader title={createAccountTitle} />
                <BlockBuilder
                  blocks={[createAccountBlock]}
                  sectionId={sectionId + 'account'}
                  options={options}
                />
              </SectionWrapper>
            )}
          </div>
        </div>
      </div>
    </SectionContainer>
  );
};

const LinkSectionHeader = ({ title }) => {
  return <div className={css.linkSectionHeader}>{title}</div>;
};

const LinkIcon = ({ children, link }) => {
  return (
    <a href={link} className={css.linkIcon}>
      {children}
    </a>
  );
};

const SectionWrapper = ({ children }) => {
  return <div className={css.sectionWrapper}>{children}</div>;
};
export default SectionFooter;
